import Link from "next/link";
import { chipsByCountry, type BoardAreaChip } from "./board-area-model";

/**
 * Surf and piste boards: a scrolling row of area chips that link to the area pages, grouped by
 * country with the busiest countries and areas first (forecast areas spec, section 10.2).
 * "All areas" goes to the hub's Browse by area block. Plain links, so crawlers can follow them.
 */
export function BoardAreaChips({
  chips,
  allAreasHref,
  sportLabel,
}: {
  chips: readonly BoardAreaChip[];
  allAreasHref: string | null;
  /** "surf" or "snow", for the accessible name. */
  sportLabel: string;
}) {
  // Link mode only: a chip without an area page has nothing to render.
  const linked = chips.filter((chip): chip is BoardAreaChip & { href: string } => chip.href !== null);
  if (linked.length === 0) return null;
  const chipClass = "inline-flex min-h-11 items-center whitespace-nowrap rounded-full border border-border-light bg-white px-3.5 py-1.5 text-[12px] font-medium text-text-body no-underline transition-colors hover:border-navy/40 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal";
  return (
    <nav aria-label={`${sportLabel} forecasts by area`} data-board-area-chips>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[1.2px] text-text-muted">
        Forecasts by area
      </div>
      <ul className="flex items-center gap-2 overflow-x-auto pb-1">
        {allAreasHref && (
          <li className="shrink-0">
            <Link href={allAreasHref} className={`${chipClass} border-navy/30 font-semibold text-navy`}>
              All areas
            </Link>
          </li>
        )}
        {chipsByCountry(linked).map((group) => (
          <li key={group.countryCode} className="flex shrink-0 items-center gap-2">
            <span className="whitespace-nowrap pl-1 text-[11px] font-semibold text-text-muted">
              {group.countryName}
            </span>
            <ul className="flex items-center gap-2">
              {group.chips.map((chip) => (
                <li key={chip.slug} className="shrink-0">
                  <Link href={chip.href} className={chipClass}>
                    {chip.name}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}
